import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { debugLog } from "../../lib/logger";

const ACCESS_NAMES = ["SpaceX", "OpenAI", "Anthropic", "Reflection AI"];
const PAPER_MULTIPLES = [
  ["Databricks", "4,043x", 4043, "2013 Series A to a $190B valuation, 2026"],
  ["Anthropic", "1,142x", 1142, "2021 Series A to $965B, 2026"],
  ["Cerebras", "~950x", 950, "2016 to a ~$95B IPO, 2026"],
  ["CoreWeave", "~950x", 950, "2019 seed to IPO, 2025"],
  ["OpenAI", "852x", 852, "$1B in 2019 to $852B, 2026"],
  ["Cursor", "~150x", 150, "2024 Series A to a $60B sale, 2026"],
  ["Cognition", "~137x", 137, "2024 Series A to $48B, 2026"],
  ["ElevenLabs", "~111x", 111, "2023 Series A to $11B, 2026"],
  ["Mistral", "~88x", 88, "2023 seed to over 21B euro, 2026"],
];
const LATE_STAGE = [
  ["Anthropic, Series E", "15.7x", "entered Mar 2025"],
  ["OpenAI, Thrive led", "5.4x", "entered Oct 2024"],
  ["OpenAI, Dec 2025", "~3.0x", "entered Dec 2025"],
  ["Anthropic, Series G", "2.3x", "entered Feb 2026"],
  ["Cursor, Series D", "~2.05x", "entered Nov 2025"],
  ["Databricks, Series L", "1.42x", "entered Dec 2025"],
  ["Statsig, Series C", "1.0x", "flat in four months"],
];
const RETURN_STATS = [
  ["4,043x", "Largest paper multiple", "Databricks' 2013 Series A, marked against a $190B valuation in 2026."],
  ["~$12.5B", "Largest realized position", "A $50M CoreWeave stake after the 2025 IPO, roughly 250x."],
  ["~2.3x", "Aggregate institutional return", "Across $27B of cost in OpenAI at $852B. The same math as most late entries."],
];

const RETURNS_FALLBACK = {
  headline: "Early money made 100x to 4,000x on paper. Late money is making 1x to 3x.",
  intro: "What the first priced round investors in today's AI leaders marked up, against what capital entering at scale has earned since. The gap between the two is not luck. It is access, and it is timing.",
  as_at: "September 2026",
  stats: RETURN_STATS.map(([value, label, desc]) => ({ value, label, desc })),
  paper: PAPER_MULTIPLES.map(([name, label, multiple, sub]) => ({ name, label, multiple, sub })),
  late: LATE_STAGE.map(([name, label, sub]) => ({ name, label, sub })),
  access_names: ACCESS_NAMES,
  access_text: "Our network has provided access to generational companies including SpaceX, OpenAI, Anthropic and Reflection AI. That access is why our members see allocations that are never offered in a public round.",
};

export default function ReturnsSection() {
  const [d, setD] = useState(RETURNS_FALLBACK);
  useEffect(() => {
    api.get("/public/returns").then((r) => setD({ ...RETURNS_FALLBACK, ...r.data })).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const barW = (m) => `${Math.max(6, Math.min(100, (Math.log10(m || 1) / Math.log10(4043)) * 100))}%`;
  const shareBrief = async () => {
    const url = window.location.origin + "/returns";
    const payload = { title: "Solera Ventures", text: "Why late stage access still wins: the AI venture returns brief.", url };
    try {
      if (navigator.share) await navigator.share(payload);
      else { await navigator.clipboard.writeText(url); toast.success("Link copied. Paste it anywhere to show the preview card."); }
    } catch (e) { debugLog("native share dismissed or failed", e); }
  };
  return (
    <section className="hairline py-16 rise" data-testid="returns-section" id="returns">
      <span className="t-label">AI venture returns, 2024 to 2026</span>
      <h2 className="text-bone mt-4 measure" style={{ fontSize: "clamp(24px, 3vw, 34px)", lineHeight: 1.15, letterSpacing: "-0.01em", maxWidth: "26ch" }}>
        {d.headline}
      </h2>
      <p className="t-small text-muted-s mt-4 measure">{d.intro}</p>

      <div className="grid sm:grid-cols-3 gap-px mt-10" style={{ background: "#24272B" }}>
        {(d.stats || []).map((s, i) => (
          <div key={s.label} className="p-6" style={{ background: "#0E1012" }} data-testid={`returns-stat-${i}`}>
            <div className="t-record !text-[40px]" style={{ color: "#C69A4E" }}>{s.value}</div>
            <span className="t-label mt-3 block">{s.label}</span>
            <p className="t-cap text-muted-s mt-2">{s.desc}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-16 mt-14">
        <div>
          <span className="t-label">First priced round to latest valuation</span>
          <p className="t-cap text-muted-s mt-2">Gross valuation step up, log scale. A ceiling for the earliest investors, not a fund level return.</p>
          <div className="mt-6 space-y-4">
            {(d.paper || []).map((row, i) => (
              <div key={row.name} data-testid={`paper-bar-${i}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="t-small text-bone">{row.name}</span>
                  <span className="fig t-small text-tint">{row.label}</span>
                </div>
                <div className="mt-1 h-[6px] w-full" style={{ background: "#181B1E" }}>
                  <div className="h-full" style={{ width: barW(row.multiple), background: "#C69A4E", transition: "width 600ms ease" }} />
                </div>
                <span className="t-cap text-muted-s">{row.sub}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <span className="t-label">What late stage entrants have made so far</span>
          <p className="t-cap text-muted-s mt-2">Entry post money to latest mark. Rounds since mid 2025 cluster at 1.0x to 2.3x.</p>
          <div className="mt-4 space-y-0">
            {(d.late || []).map((row, i) => (
              <div key={row.name} className="hairline py-3 flex items-baseline justify-between gap-4 first:border-t-0 first:pt-0" data-testid={`late-row-${i}`}>
                <div>
                  <span className="t-small text-bone">{row.name}</span>
                  <span className="t-cap text-muted-s ml-3">{row.sub}</span>
                </div>
                <span className="fig t-small text-muted-s">{row.label}</span>
              </div>
            ))}
          </div>
          <div className="gapped-enclosure p-6 mt-8" data-testid="returns-access">
            <span className="t-label">Access, not capital, is the edge</span>
            <p className="t-small text-muted-s mt-3 measure">
              In today's market the largest tenders sell only two thirds of what is authorized, and most
              secondary volume sits in a handful of names. The constraint is not money. It is a seat at the table.
            </p>
            <p className="t-small text-bone mt-4 measure">{d.access_text}</p>
            <div className="flex gap-2 flex-wrap mt-5">
              {(d.access_names || []).map((n, i) => (
                <span key={n} className="t-label px-3 py-1 border border-[#24272B]" data-testid={`access-name-${i}`}>{n}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="hairline mt-12 pt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6" data-testid="returns-invitation">
        <div>
          <span className="t-label">By invitation</span>
          <p className="t-small text-muted-s mt-2 measure">
            Solera is a private membership. If a member has referred you, complete your registration.
            Otherwise, write to us and tell us plainly why there is a fit.
          </p>
        </div>
        <div className="flex gap-4 shrink-0 flex-wrap">
          <Link to="/register" className="btn-primary inline-block" data-testid="returns-register-button">Complete registration</Link>
          <a href="/#contact" className="btn-ghost inline-block" data-testid="returns-contact-button">Write to Solera</a>
          <button onClick={shareBrief} className="btn-ghost inline-block" data-testid="returns-share-button">Share this brief</button>
        </div>
      </div>

      <p className="t-cap text-muted-s mt-10 measure">
        Figures are third party market data compiled from public sources as at {d.as_at}. Multiples are
        gross valuation step ups, pre dilution and pre fees, and overstate what any single investor booked.
        Past performance is not indicative of future results. Nothing here is an offer to buy or sell securities.
      </p>
    </section>
  );
}
